import type { DrawContext } from './draw';
import type { View } from './project';
import type { Random } from './rng';

/*
 * ── WHAT IS OUT THERE, PAST EVERYTHING ───────────────────────────────────────────────────────────
 *
 * OURS, NOT A PORT. Theirs fills the space past the rim with fifty-two clouds of dust — soft white
 * ellipses, each turned by its own angle and squashed into the world's plane, drifting at half the
 * field's spin. It is faithfully ported in `void.ts` and it was drawn here until 2026-08-20, when
 * Ariel asked for it to go: "remove those white washed skewed shapes outside the most outer ring,
 * it looks bad, just make sure it looks like there are other stars in the distance so it is not
 * completely void, but make it very slight, and disconnect it from the rings layer, it should not
 * spin with the star."
 *
 * So this is what stands in its place, and every clause of that is a decision here:
 *
 * DISTANT, WHICH IS WHY IT DOES NOT SPIN. The field turns; the sky behind it does not. There is no
 * spin argument to pass and no way to pass one — the layer is drawn in SCREEN space, so the world's
 * rotation cannot reach it. `static-frame.test.ts` renders the same frame at two spin angles and
 * asks for the same calls back.
 *
 * BUT NOT PAINTED ON THE GLASS. Parallax falls off with distance and does not reach zero, so a pan
 * slides these by a thirtieth of what it slides the map — `FAR_PARALLAX`, against the 0.2 the near
 * motes in `clouds.ts` get. Measured against how far the world moved ON SCREEN (`cam.x * z`) rather
 * than in world units: the same pan is a longer slide when the reader is close in, and a star that
 * ignored that would drift the wrong way as the zoom changed. Size and brightness are what distance
 * does settle, and those the zoom never touches.
 *
 * VERY SLIGHT. The brightest star in the field is 0.26 alpha before the fade, and most are a
 * quarter of that: the depth is raised to a power, so a handful sit near and the rest recede.
 *
 * IT ARRIVES WHERE THE DUST DID. `voidFade` again, measured against the reader's zoom floor rather
 * than an absolute zoom — the outside is something a reader finds by pulling back.
 *
 * SEEDED, SO IT HOLDS STILL. A field regenerated per frame would shimmer, which is the mistake the
 * seed in `void.ts` was there to prevent and the reason for the one here.
 */

/** Their turn, in radians, as everywhere else in this port. */
const TAU = 6.2832;

/** One star: where it sits on the viewport, and how far off it is. */
export interface FarStar {
  /** Its place, as a fraction of the viewport — so the field fits any window. */
  fx: number;
  fy: number;
  /** 0 is the nearest of the far ones and 1 the furthest. Sets size, brightness and parallax at once. */
  depth: number;
}

/** The seed. Arbitrary, and therefore mechanism: it must simply be the same one every session. */
export const FAR_STAR_SEED = 30707;

/**
 * How many.
 *
 * A density over the SCREEN rather than over the world, so it is not a world dimension and cannot be
 * mis-scaled against the rim — which is precisely the trap the dust fell into. `ourDust` sized a
 * cloud as a share of the BAND'S WIDTH, `span * (0.18 + random() * 0.3)`, so on a rim of 4,790 the
 * mean cloud came out 736 across: 15.4% of the rim against the 0.55% theirs draws, and 0.66 of the
 * distance to the next cloud against their 0.05. Twenty-six overlapping blobs, each a fifth of the
 * map wide. See `ourFarStars` in `our-scene.ts` for the measurement and how it was nearly missed.
 */
export const FAR_STAR_COUNT = 170;

/** How much a pan moves a star, per unit of nearness. A thirtieth of the map's own movement. */
const FAR_PARALLAX = 0.035;

/** Size in CSS pixels, nearest to furthest. Under one pixel for most of the field. */
const SIZE_NEAR = 1.05;
const SIZE_FAR = 0.35;

/** Alpha before the fade, nearest to furthest. */
const BRIGHT_NEAR = 0.26;
const BRIGHT_FAR = 0.055;

/**
 * How the depths are spread.
 *
 * BELOW 1 biases toward the FAR end, so most of the field is faint and a few stars carry it — an
 * even spread reads as noise of one weight, which is the look this layer is replacing. Below,
 * because depth 0 is the near and bright end and `Math.pow` on [0,1) pushes values DOWN: the first
 * try wrote 1.7 with a comment claiming the far end, and it made two thirds of the field bright.
 * The test asking for a median under a tenth is what found it.
 */
const DEPTH_BIAS = 0.45;

/**
 * The colour. Look, not data — the same cold blue-white the rest of the field is lit in.
 *
 * Exported because the frame's differential test finds this layer BY COLOUR, the way it finds
 * theirs, and a test that retyped the string would keep passing after the colour changed.
 */
export const FAR_STAR_COLOUR = '#c9d6f2';

/** Below this much of the fade the layer is not worth a draw call. */
export const FAR_STAR_MIN_FADE = 0.02;

/** Build the field once. Seeded, so it is the same stars every session. */
export function buildFarStars(random: Random, count = FAR_STAR_COUNT): FarStar[] {
  const stars: FarStar[] = [];
  for (let i = 0; i < count; i++) {
    stars.push({
      fx: random(),
      fy: random(),
      depth: Math.pow(random(), DEPTH_BIAS),
    });
  }
  return stars;
}

/** A true modulo. `%` keeps the sign, which would put a star panned off the left edge nowhere. */
function wrap(v: number, m: number): number {
  return ((v % m) + m) % m;
}

/** Blend from the near value to the far one. */
function byDepth(near: number, far: number, depth: number): number {
  return near + (far - near) * depth;
}

/**
 * Draw the far field. Returns how many stars were drawn, so a caller can prove it ran.
 *
 * No spin argument, by design: see the head of this file. All the camera does to this layer is
 * slide it by `FAR_PARALLAX`; nothing here reads a size or a brightness off the zoom.
 */
export function drawFarStars(
  ctx: DrawContext,
  view: View,
  stars: readonly FarStar[],
  fade: number,
): number {
  if (fade <= FAR_STAR_MIN_FADE) return 0;
  const w = view.view.width;
  const h = view.view.height;
  const z = view.cam.z;
  if (w <= 0 || h <= 0) return 0;

  ctx.fillStyle = FAR_STAR_COLOUR;
  for (const star of stars) {
    /* Nearness, so a star at depth 0 slides most and one at depth 1 barely at all. */
    const near = 1 - star.depth;
    const x = wrap(star.fx * w - view.cam.x * z * FAR_PARALLAX * near, w);
    const y = wrap(star.fy * h - view.cam.y * z * FAR_PARALLAX * near, h);
    ctx.globalAlpha = byDepth(BRIGHT_NEAR, BRIGHT_FAR, star.depth) * fade;
    ctx.beginPath();
    ctx.arc(x, y, byDepth(SIZE_NEAR, SIZE_FAR, star.depth), 0, TAU);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
  return stars.length;
}
